/**
 * Configuracao do router.
 *
 * - Combina rotas estaticas (`routes.js`) com rotas dinamicas convencionadas
 *   em `FullRoutes.js` (carregadas a partir de `views/app/**`).
 * - Intercepta respostas 401 e tenta um refresh transparente uma unica vez,
 *   antes de redirecionar para `/login`.
 * - Aplica guard `authRequired` baseado no estado de auth do Vuex.
 */

import { createWebHistory, createRouter } from 'vue-router';
import { definirTenantAtivo } from '@/services/tenantAtivo';
import axios from 'axios';
import routes from './routes';
import FullRoutes from './FullRoutes';
import appConfig from '../../app.config.json';
import store from '@/state/store';
import resolveUrl from '@/services/resolveUrl';

// VUE_APP_APIURL pode usar {host} pra resolver o hostname em runtime.
// Ex.: VUE_APP_APIURL=https://{host}:8443
axios.defaults.baseURL = resolveUrl(process.env.VUE_APP_APIURL);
axios.defaults.withCredentials = true;

const REFRESH_ENDPOINT = '/auth/refresh';
let refreshPromise = null;
let isRedirectingToLogin = false;

function redirectToLoginOnce() {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  store.dispatch('auth/clearCurrentUser');
  window.location.href = '/login';
}

function isRefreshRequest(config) {
  const url = config?.url ? String(config.url) : '';
  return url.includes(REFRESH_ENDPOINT);
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status ?? null;
    const originalRequest = error?.config;
    if (status !== 401 || !originalRequest) return Promise.reject(error);
    if (isRefreshRequest(originalRequest) || originalRequest._retry) return Promise.reject(error);

    if (!store.getters['auth/loggedIn']) {
      redirectToLoginOnce();
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = axios.post(REFRESH_ENDPOINT, {}, { withCredentials: true })
          .finally(() => { refreshPromise = null; });
      }
      await refreshPromise;
      return axios(originalRequest);
    } catch (refreshError) {
      // eslint-disable-next-line no-console
      console.error('Erro ao renovar token:', refreshError);
      redirectToLoginOnce();
      return Promise.reject(refreshError);
    }
  },
);

// Une rotas estaticas com as dinamicas (sem duplicar nomes).
const routeNames = new Set(routes.map((r) => r.name));
const uniqueFullRoutes = FullRoutes.filter((r) => !routeNames.has(r.name));
const combinedRoutes = routes.concat(uniqueFullRoutes);

const router = createRouter({
  history: createWebHistory(),
  routes: combinedRoutes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0, left: 0 };
  },
});

/**
 * Link de espaço: `/t/<id>/resto` → guarda o espaço e segue para `/resto`.
 *
 * O backend remove esse prefixo antes de rotear, mas o navegador não: sem esta
 * regra, abrir `/t/2/` daria tela em branco, porque nenhuma rota do Vue casa
 * com `/t/...`. É o que torna o link de um espaço compartilhável.
 *
 * `/t/2` sozinho leva para a raiz do app dentro daquele espaço.
 */
router.beforeEach((routeTo, routeFrom, next) => {
  const casa = /^\/t\/(\d+)(\/.*)?$/.exec(routeTo.fullPath);
  if (casa) {
    definirTenantAtivo(Number(casa[1]));
    return next(casa[2] && casa[2] !== '/' ? casa[2] : '/');
  }
  return next();
});

router.beforeEach((routeTo, routeFrom, next) => {
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  if (!authRequired) return next();

  if (store.getters['auth/loggedIn']) return next();
  return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          resolve();
        }
      });
    }
  } catch (_error) {
    return;
  }
  document.title = `${routeTo.meta.title || ''} | ${appConfig.title}`;
  next();
});

export default router;
